import AIToolEmbed from "@/app/components/atomic/molecules/AIToolEmbed";
import ComparatorEmbed from "@/app/components/atomic/molecules/ComparatorEmbed";
import { processFlourishEmbed } from "@/app/components/atomic/molecules/FlourishEmbed";
import ToolEmbed from "@/app/components/atomic/molecules/ToolEmbed";
import { getShortcodeAttribute } from "@/app/utils/shortcode-attributes";
import YouTubeEmbed, { processYouTubeIframe } from "@/app/components/atomic/molecules/YoutubeEmbed";
import YouTubeStructuredData from "@/app/components/atomic/molecules/YoutubeStructuredData";
import ExpertComment from "@/app/components/atomic/molecules/ExpertComment";
import Methodology from "@/app/components/atomic/molecules/Methodology";
import TempsTravailCreditLazy from "@/app/components/atomic/molecules/TempsTravailCreditLazy";
import CompteurLoiLemoineLazy from "@/app/components/atomic/molecules/CompteurLoiLemoineLazy";
import PercentileAssurancePretLazy from "@/app/components/atomic/molecules/PercentileAssurancePretLazy";
import OtherSources from "@/app/components/atomic/molecules/OtherSources";
import { useTracking } from '@/app/contexts/tracking-context';
import type { MarketRate } from "@/app/utils/shortcode-preprocessing";
import type { LemoineSeries } from "@/app/utils/lemoine-counter";
import parse, { DOMNode, HTMLReactParserOptions, Element, attributesToProps, domToReact } from 'html-react-parser';
import { useEffect, useMemo, type ReactNode } from "react";

interface WPContentRendererProps {
    html: string;
    preprocessedTools?: Array<{
        id: string;
        showFiltersAbove?: boolean;
        toolData: any;
        template: any;
        filters: any[];
        expressionContext?: string;
        expressionOnLoad?: string;
    }>;
    preprocessedComparators?: Array<{
        id: string;
        showFiltersAbove?: boolean;
        filterListClasses?: string;
        gapBetweenFilters?: string;
        templateConfig: any;
        filtersConfig: any;
        initialResults: any[];
    }>;
    marketRate?: MarketRate | null;
    lemoineSeries?: LemoineSeries | null;
}

const EDITORIAL_TOOL_SHORTCODE = /\[(rm_outil_temps_travail_credit|rm_outil_compteur_loi_lemoine|rm_outil_percentile_taea)\b([^\]]*)\]/g;

const containsEditorialTool = (node: DOMNode): boolean =>
    node.type === 'text'
        ? node.data.search(EDITORIAL_TOOL_SHORTCODE) !== -1
        : node instanceof Element && node.children.some((child) => containsEditorialTool(child as DOMNode));

// These shortcodes are not registered in WordPress, so wptexturize may curl the quotes around the attribute value
const COMPACT_FORMAT = /\bformat\s*=\s*["'“”«»\s]*compact(?=["'“”«»\s]|$)/i;

// Splits the text node at each tool shortcode in reading order, keeping the words and tools around it on the page
const renderEditorialTools = (domNode: DOMNode, marketRate?: MarketRate | null, lemoineSeries?: LemoineSeries | null) => {
    const text = domNode.type === 'text' ? domNode.data ?? '' : '';
    const matches = Array.from(text.matchAll(EDITORIAL_TOOL_SHORTCODE));
    if (matches.length === 0) {
        return domNode;
    }

    const parts: ReactNode[] = [];
    let cursor = 0;
    for (const match of matches) {
        const [shortcode, name, attributes] = match;
        const start = match.index ?? 0;
        const compact = COMPACT_FORMAT.test(attributes);
        const key = `${name}-${start}`;

        parts.push(text.slice(cursor, start));
        parts.push(
            name === 'rm_outil_temps_travail_credit' ? (
                <TempsTravailCreditLazy key={key} marketRate={marketRate ?? undefined} compact={compact} />
            ) : name === 'rm_outil_compteur_loi_lemoine' ? (
                <CompteurLoiLemoineLazy key={key} series={lemoineSeries} compact={compact} />
            ) : (
                <PercentileAssurancePretLazy key={key} compact={compact} />
            )
        );
        cursor = start + shortcode.length;
    }
    parts.push(text.slice(cursor));

    return <>{parts}</>;
};

const getShortcode = (domNode: DOMNode, shortcodeName: string): string | undefined => {
    if (domNode.type !== 'text') return undefined;
    return domNode.data.match(new RegExp(`\\[${shortcodeName}\\s[^\\]]*\\]`))?.[0];
};

const shortCodeProcessor = {
    processYouTubeEmbed: (domNode: DOMNode) => {
        if (domNode.type === 'text') {
            // format="short" is optional so existing shortcodes keep rendering as 16:9
            const regex = /\[youtube_embed id="(.+?)" title="(.+?)" description="(.+?)"(?: format="(.+?)")?\]/;
            const match = domNode.data.match(regex);
            if (match) {
                const [, id, title, description, format] = match;
                return (
                    <>
                        <YouTubeStructuredData
                            id={id}
                            description={description}
                            title={title}
                        />
                        <YouTubeEmbed id={id} isShort={format === 'short'} />
                    </>
                );
            }
        }

        return domNode;
    },
    processAiToolEmbed: (domNode: DOMNode) => {
        if (domNode.type === 'text') {
            const regex = /\[ai_tool_embed toolFileName="(.+?)" prerenderFileName="(.+?)"]/;
            const match = domNode.data?.match(regex);
            if (match) {
                const [fullMatch, toolFileName, prerenderFileName] = match;
                return <AIToolEmbed toolFileName={toolFileName} prerenderFileName={prerenderFileName} />;
            }
        }
        return domNode;
    },
    processExpertComment: (domNode: DOMNode) => {
        if (domNode.type === 'text') {
            const regex = /\[expert_comment firstName=\\?"([^\\"]*?)\\?" lastName=\\?"([^\\"]*?)\\?" jobTitle=\\?"([^\\"]*?)\\?" authorPageUrl=\\?"([^\\"]*?)\\?" authorPageImg=\\?"([^\\"]*?)\\?" clickable=\\?"([^\\"]*?)\\?" comment=\\?"([^\\"]*?)\\?"]/;
            const match = domNode.data?.match(regex)

            if (match) {
                const [, firstName, lastName, jobTitle, authorPageUrl, authorPageImg, clickable, comment] = match;
                return (
                    <ExpertComment
                        firstName={firstName}
                        lastName={lastName}
                        authorPageUrl={authorPageUrl}
                        authorPageImg={authorPageImg}
                        jobTitle={jobTitle}
                        clickable={clickable === 'true'}
                        comment={comment}
                    />
                );
            }
        }
        return domNode;
    },
    processMethodology: (domNode: DOMNode) => {
        if (domNode.type === 'text') {
            const regex = /\[methodology content=\\?"([^\\"]+?)\\?"]/;
            const match = domNode.data?.match(regex);
            if (match) {
                const [, content] = match;
                return <Methodology content={content} />;
            }
        }
        return domNode;
    },
    processOtherSources: (domNode: DOMNode) => {
        if (domNode.type === 'text') {
            const regex = /\[other_sources intro=\\?"([^\\"]+?)\\?" sources=\\?"([\s\S]+?)\\?"]/;
            const match = domNode.data?.match(regex);
            if (match) {
                const [, intro, sourcesJson] = match;
                try {
                    const cleanedJson = sourcesJson.includes('\\"') ? sourcesJson.replace(/\\"/g, '"') : sourcesJson;
                    const sources = JSON.parse(cleanedJson);
                    return <OtherSources intro={intro} sources={sources} />;
                } catch (error) {
                    console.error('Error parsing sources JSON:', error);
                    return domNode;
                }
            }
        }
        return domNode;
    },
    processComparatorEmbed: (domNode: DOMNode, preprocessedComparators?: WPContentRendererProps['preprocessedComparators']) => {
        const shortcode = getShortcode(domNode, 'auctor_comparator_embed')
        const id = shortcode && getShortcodeAttribute(shortcode, 'id')

        if (id) {
            // Check if we have preprocessed data for this comparator
            const preprocessedComparator = preprocessedComparators?.find(comp => comp.id === id);

            if (preprocessedComparator) {
                // Use server-side rendered ComparatorEmbedSSR component
                return (
                    <ComparatorEmbedSSR
                        comparatorId={id}
                        showFiltersAbove={preprocessedComparator.showFiltersAbove}
                        filterListClasses={preprocessedComparator.filterListClasses}
                        gapBetweenFilters={preprocessedComparator.gapBetweenFilters}
                        templateConfig={preprocessedComparator.templateConfig}
                        filtersConfig={preprocessedComparator.filtersConfig}
                        initialResults={preprocessedComparator.initialResults}
                    />
                );
            }

            // Fallback to client-side component if no preprocessing available
            return (
                <ComparatorEmbed
                    comparatorId={id}
                    showFiltersAbove={true}
                />
            );
        }
        return domNode;
    },
    processToolEmbed: (domNode: DOMNode, preprocessedTools?: WPContentRendererProps['preprocessedTools']) => {
        const shortcode = getShortcode(domNode, 'auctor_tool_embed')
        const id = shortcode && getShortcodeAttribute(shortcode, 'id')
        const title = shortcode && getShortcodeAttribute(shortcode, 'title')

        if (id) {
            // Check if we have preprocessed data for this tool
            const preprocessedTool = preprocessedTools?.find(tool => tool.id === id);

            if (preprocessedTool) {
                // Use server-side rendered ToolEmbedSSR component
                return (
                    <ToolEmbedSSR
                        toolId={id}
                        title={title}
                        showFiltersAbove={true}
                        toolData={preprocessedTool.toolData}
                        template={preprocessedTool.template}
                        filters={preprocessedTool.filters}
                        expressionContext={preprocessedTool.expressionContext}
                        expressionOnLoad={preprocessedTool.expressionOnLoad}
                    />
                );
            }

            // Fallback to client-side component if no preprocessing available
            return (
                <ToolEmbed
                    toolId={id}
                    title={title}
                    showFiltersAbove={true}
                />
            );
        }

        return domNode;
    }
}

// Server-side rendered ToolEmbed component that doesn't require JavaScript
import Tool from "@/app/components/auctor/tool/Tool";
import {TOOL_ANCHOR_CLASS, toolAnchorId} from "@/app/utils/tool-anchor";

interface ToolEmbedSSRProps {
    toolId: string;
    title?: string;
    showFiltersAbove?: boolean;
    toolData: any;
    template: any;
    filters: any[];
    expressionContext?: string;
    expressionOnLoad?: string;
}

function ToolEmbedSSR({
    toolId,
    title,
    showFiltersAbove = false,
    toolData,
    template,
    filters,
    expressionContext,
    expressionOnLoad,
}: ToolEmbedSSRProps) {
    if (!template) {
        return (
            <div className="w-full bg-yellow-100 text-yellow-900 py-4 px-4 rounded-lg border border-yellow-300">
                <div className="flex justify-center items-center">
                    <div className="text-sm">
                        Outil non disponible
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id={toolAnchorId(toolId)} className={`mb-8 mt-4 w-full ${TOOL_ANCHOR_CLASS}`}>
            <Tool
                title={title}
                template={template}
                filters={filters}
                toolData={toolData}
                expressionContext={expressionContext}
                expressionOnLoad={expressionOnLoad}
                showFiltersAbove={showFiltersAbove}
                mobileTemplateSticky={false}
                isPreview={false}
                isEmbedded={true}
            />
        </div>
    );
}

// Server-side rendered ComparatorEmbed component that doesn't require JavaScript
import Comparator from "@/app/components/auctor/comparator/Comparator";

interface ComparatorEmbedSSRProps {
    comparatorId: string;
    showFiltersAbove?: boolean;
    filterListClasses?: string;
    gapBetweenFilters?: string;
    templateConfig: any;
    filtersConfig: any;
    initialResults: any[];
}

function ComparatorEmbedSSR({
    comparatorId,
    showFiltersAbove = false,
    templateConfig,
    filtersConfig,
    initialResults,
}: ComparatorEmbedSSRProps) {
    if (!templateConfig || !filtersConfig) {
        return (
            <div className="w-full bg-yellow-100 text-yellow-900 py-4 px-4 rounded-lg border border-yellow-300">
                <div className="flex justify-center items-center">
                    <div className="text-sm">
                        Comparateur non disponible
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-8 mt-4 w-full">
            <Comparator
                comparatorId={parseInt(comparatorId)}
                templateConfig={templateConfig}
                filtersConfig={filtersConfig}
                initialResults={initialResults}
                showFiltersAbove={showFiltersAbove}
                isPreview={false}
            />
        </div>
    );
}

const shortcodeProcessors: { [key: string]: (domNode: DOMNode, preprocessedTools?: WPContentRendererProps['preprocessedTools'], preprocessedComparators?: WPContentRendererProps['preprocessedComparators'], marketRate?: MarketRate | null, lemoineSeries?: LemoineSeries | null) => any } = {
    '[youtube_embed': (domNode) => shortCodeProcessor.processYouTubeEmbed(domNode),
    '[ai_tool_embed': (domNode) => shortCodeProcessor.processAiToolEmbed(domNode),
    '[auctor_comparator_embed': (domNode, preprocessedTools, preprocessedComparators) => shortCodeProcessor.processComparatorEmbed(domNode, preprocessedComparators),
    '[auctor_tool_embed': (domNode, preprocessedTools) => shortCodeProcessor.processToolEmbed(domNode, preprocessedTools),
    '[expert_comment': (domNode) => shortCodeProcessor.processExpertComment(domNode),
    '[methodology': (domNode) => shortCodeProcessor.processMethodology(domNode),
    '[other_sources': (domNode) => shortCodeProcessor.processOtherSources(domNode),
    '[rm_outil_temps_travail_credit': (domNode, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries) => renderEditorialTools(domNode, marketRate, lemoineSeries),
    '[rm_outil_compteur_loi_lemoine': (domNode, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries) => renderEditorialTools(domNode, marketRate, lemoineSeries),
    '[rm_outil_percentile_taea': (domNode, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries) => renderEditorialTools(domNode, marketRate, lemoineSeries),
};

const WPContentRenderer = ({ html, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries }: WPContentRendererProps) => {
    const { track } = useTracking();

    const processedHtml = useMemo(() => {
        const parserOptions: HTMLReactParserOptions = {
            replace: (domNode: DOMNode) => {
                // Handle Flourish embeds (must check before text nodes since it's an element)
                const flourishResult = processFlourishEmbed(domNode);
                if (flourishResult) {
                    return flourishResult;
                }

                // Raw YouTube iframes pasted by editors also need to be deferred until click
                const youtubeIframeResult = processYouTubeIframe(domNode);
                if (youtubeIframeResult) {
                    return youtubeIframeResult;
                }

                // The tools render blocks, which a <p> cannot hold: the browser would reflow it and break hydration
                if (domNode instanceof Element && domNode.name === 'p' && containsEditorialTool(domNode)) {
                    return (
                        <div {...attributesToProps(domNode.attribs)}>
                            {domToReact(domNode.children as DOMNode[], parserOptions)}
                        </div>
                    );
                }

                if (domNode.type === 'text') {
                    for (const [shortcode, processor] of Object.entries(shortcodeProcessors)) {
                        if (domNode.data?.includes(shortcode)) {
                            return processor(domNode, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries);
                        }
                    }
                }

                if (domNode instanceof Element && /^h[1-4]$/.test(domNode.name)) {
                    const isFaqQuestion = (domNode.attribs.class || '').split(/\s+/).includes('schema-faq-question')
                    if (!isFaqQuestion) {
                        const Tag = domNode.name as 'h1' | 'h2' | 'h3' | 'h4'
                        return (
                            <div className="mt-6 mb-2">
                                <Tag {...attributesToProps(domNode.attribs)}>
                                    {domToReact(domNode.children as DOMNode[], parserOptions)}
                                </Tag>
                            </div>
                        )
                    }
                }

                return domNode;
            },
        };
        return parse(html, parserOptions);
    }, [html, preprocessedTools, preprocessedComparators, marketRate, lemoineSeries]);

    // Add click tracking for wp-block-rm-advertizing-button elements within the slug itself
    useEffect(() => {
        const handleAdvertisingButtonClick = (event: Event) => {
            const target = event.target as HTMLElement;

            const hasAdvertisingButtonClass = Array.from(target.classList).some(className =>
                className.includes('wp-block-rm-advertizing-button')
            );

            const hasWpButtonClass = Array.from(target.classList).some(className =>
                className.includes('wp-block-button')
            );

            if (target && (hasAdvertisingButtonClass || hasWpButtonClass)) {
                track({
                    event: 'wp_advertizing_button_click',
                    type: hasAdvertisingButtonClass ? 'cta_advertiwing_block_intext' : 'cta_intext'
                });
            }
        };

        // Use event delegation on the content container
        const contentElement = document.getElementById('content');
        if (contentElement) {
            contentElement.addEventListener('click', handleAdvertisingButtonClick);
        }

        // Cleanup event listener
        return () => {
            if (contentElement) {
                contentElement.removeEventListener('click', handleAdvertisingButtonClick);
            }
        };
    }, [track, processedHtml]);

    return (
        <article id="content" className="post-content-v2">
            {processedHtml}
        </article>
    );
};

export default WPContentRenderer;
